import { Button, Stack } from "@mui/material";
import { useSelector } from "react-redux";
import { calculatePolygonCenter } from "../../map/mapUtils";
import { useEffect, useState } from "react";
import DevicePositionDialog from "../../muiDialogs/devicePositionDialog.component";
import { getLocation } from "../../../utils/gpsLocationUtils";
import { setNewPosition } from "../../../store/features/customer/marker/markerSlice";
import { setLocationMode } from "../../../store/features/customer/marker/dragModeSlice";

const isValidGpsPosition = (position) =>
  Array.isArray(position) &&
  position.length === 2 &&
  position.every((value) => Number.isFinite(Number(value)));

const isSameId = (left, right) =>
  left !== undefined &&
  left !== null &&
  right !== undefined &&
  right !== null &&
  String(left) === String(right);

const ControlButton = ({ onClick, label, color, disabled = false }) => {
  return (
    <Button
      variant="outlined"
      fullWidth
      size="small"
      onClick={onClick}
      disabled={disabled}
      sx={{ textTransform: "none" }}
      color={color}
    >
      {label}
    </Button>
  );
};

const SensorControls = ({
  instrumentsData,
  field,
  isHover,
  onDragModeStart,
  onDragModeStop,
  onFirstPosition,
  onSavePosition,
  dispatch,
}) => {
  const { newPosition } = useSelector((state) => state.marker);
  const { hoveredSensorId } = useSelector((state) => state.sensor?.isHover);
  const { isActive: isDragModeActive, draggableSensorId } = useSelector(
    (state) => state.dragMode
  );
  const { isLoading: isSensorSaving } = useSelector((state) => state.sensor);
  const accessToken = useSelector((state) => state.auth?.user?.access_token);

  const hasGpsPosition = isValidGpsPosition(instrumentsData?.gps_position);
  const [open, setOpen] = useState(false);
  const [location, setLocation] = useState(null);

  const isCurrentSensorHovered = isSameId(hoveredSensorId, instrumentsData.id);
  const isSensorInDragMode = isSameId(draggableSensorId, instrumentsData.id);
  const isAnotherSensorDragging = isDragModeActive && !isSensorInDragMode;
  const handleDragMode = () => {
    if (isValidGpsPosition(instrumentsData?.gps_position)) {
      dispatch(setNewPosition(instrumentsData.gps_position));
    }
    onDragModeStart(instrumentsData.id);
  };
  const handleDialogChoice = (choice) => {
    if (choice === "device" && location) {
      const position = [location.latitude, location.longitude];

      dispatch(setNewPosition(position));
      dispatch(setLocationMode(true));
      onFirstPosition(instrumentsData.id, position);
    } else if (choice === "handPosition") {
      const position = calculatePolygonCenter(field?.polygon);
      if (isValidGpsPosition(position)) {
        dispatch(setNewPosition(position));
        dispatch(setLocationMode(false));
        onFirstPosition(instrumentsData.id, position);
      }
    }
    setOpen(false);
  };

  const handleSavePosition = () => {
    if (!accessToken) return;

    const positionToSave = isValidGpsPosition(newPosition)
      ? newPosition
      : isValidGpsPosition(instrumentsData?.gps_position)
        ? instrumentsData.gps_position
        : calculatePolygonCenter(field?.polygon);

    if (!isValidGpsPosition(positionToSave)) {
      return;
    }

    const sensorType =
      instrumentsData.sensor_type?.id || instrumentsData.sensor_type;

    const sensorData = {
      nickname: instrumentsData.nickname,
      devEUI: instrumentsData.devEUI,
      appEUI: instrumentsData.appEUI,
      appKey: instrumentsData.appKey,
      update_frequency: instrumentsData.update_frequency,
      sensor_type: sensorType,
      field: field?.id,
      gps_position: positionToSave,
    };
    onSavePosition(sensorData, accessToken, instrumentsData.id);
  };
  const handleStopDragMode = () => {
    onDragModeStop();
  };

  useEffect(() => {
    if (open) {
      getLocation(null, setLocation);
    }
  }, [open]);

  return (
    <>
      {!isAnotherSensorDragging &&
        !isDragModeActive &&
        ((isHover && hasGpsPosition) ||
          (isCurrentSensorHovered && hasGpsPosition)) && (
        <ControlButton onClick={handleDragMode} label="Riposiziona" />
      )}
      {!hasGpsPosition && !isDragModeActive && (
        <ControlButton
          onClick={() => setOpen(true)}
          label="Posiziona sulla mappa"
        />
      )}
      {isSensorInDragMode && (
        <Stack direction="row" spacing={1}>
          <ControlButton
            onClick={handleSavePosition}
            label="Salva"
            disabled={isSensorSaving}
          />
          <ControlButton
            onClick={handleStopDragMode}
            label="Annulla"
            color="error"
            disabled={isSensorSaving}
          />
        </Stack>
      )}
      {open && (
        <DevicePositionDialog
          open={open}
          handleClose={() => setOpen(false)}
          onChoiseSelected={handleDialogChoice}
        />
      )}
    </>
  );
};

export default SensorControls;
